import {
  AlarmIcon,
  CameraIcon,
  DoorIcon,
  GuardsIcon,
  MailIcon,
  ManualIcon,
  QuickReportIcon,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import {
  DeviceType,
  IncidentSource,
  IncidentType,
  RuleEventType,
} from '@/graphql/generated/schemas'

import { IncidentTypeSlug } from './incidents'

const resolveIconColor = (colorScheme: ColorScheme) =>
  colorScheme === 'light'
    ? 'var(--hw-dark-colors-light-icon-secondary)'
    : 'var(--hw-dark-colors-icon-secondary)'

const normalizeString = (value: string | null | undefined): string =>
  value
    ? value.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
    : ''

// Legacy IncidentIcon special case: a type-level Alarm whose name matches the
// "Door Open Alarm" rule event renders the door glyph rather than the generic
// alarm. normalizeString maps both the RuleEventType.DoorOpenAlarm value
// ('DOOR_OPEN_ALARM') and the human "Door Open Alarm" label to 'door open
// alarm', so comparing against the enum value avoids importing the label.
const isDoorOpenAlarmName = (name: string | null | undefined): boolean => {
  const normalized = normalizeString(name)
  return (
    normalized !== '' &&
    normalized === normalizeString(RuleEventType.DoorOpenAlarm)
  )
}

function getIconForDeviceType(
  deviceType: DeviceType | null | undefined,
  colorScheme: ColorScheme
): React.ReactNode {
  const color = resolveIconColor(colorScheme)
  switch (deviceType) {
    case DeviceType.Door:
      return <DoorIcon color={color} />
    case DeviceType.Camera:
      return <CameraIcon color={color} />
    case DeviceType.Alarm:
      return <AlarmIcon color={color} />
    default:
      return <ManualIcon colorScheme={colorScheme} />
  }
}

/**
 * Maps an incident's type / slug / source to a dark-package detection icon.
 * Shared by the incident drawer (IncidentCard) and the notifications drawer
 * (ActiveIncidentCard) so rebranded incident surfaces render the same glyphs.
 * `name`, `deviceType`, `source` and `creatorRole` are optional — surfaces that
 * don't fetch them degrade to the type/slug branches and the ManualIcon
 * fallback. `name` restores the legacy Door-Open-Alarm glyph special case.
 */
export function getDetectionTypeIcon(
  type: IncidentType | null | undefined,
  slug: string | null | undefined,
  isManual: boolean,
  colorScheme: ColorScheme,
  name?: string | null,
  deviceType?: DeviceType | null,
  source?: IncidentSource | null,
  creatorRole?: string | null
): React.ReactNode {
  const color = resolveIconColor(colorScheme)
  if (
    source === IncidentSource.QuickReport ||
    source === IncidentSource.CustomQuickReport
  )
    return <QuickReportIcon colorScheme={colorScheme} />
  if (
    source === IncidentSource.Sensoscientific ||
    source === IncidentSource.HoneywellMassComms
  )
    return <MailIcon colorScheme={colorScheme} />
  if (isManual) {
    return creatorRole === 'GUARD' ? (
      <GuardsIcon colorScheme={colorScheme} />
    ) : (
      <ManualIcon colorScheme={colorScheme} />
    )
  }

  // Slug-based mapping takes precedence when useIncidentTypeDefinition is on
  if (slug) {
    switch (slug) {
      case IncidentTypeSlug.DoorForcedOpen:
      case IncidentTypeSlug.DoorHeldOpenStarted:
      case IncidentTypeSlug.DoorOpenAlarm:
      case IncidentTypeSlug.DoorUnlockedTailgate:
        return <DoorIcon color={color} />
      case IncidentTypeSlug.LineCrossing:
        return <CameraIcon color={color} />
      case IncidentTypeSlug.Alarm:
        return <AlarmIcon color={color} />
      case IncidentTypeSlug.DeviceHealth:
      case IncidentTypeSlug.DeviceOffline:
        return getIconForDeviceType(deviceType, colorScheme)
      default:
        return <ManualIcon colorScheme={colorScheme} />
    }
  }

  switch (type) {
    case IncidentType.ForcedEntry:
    case IncidentType.DoorHeldOpen:
    case IncidentType.DoorHeldClose:
    case IncidentType.Tailgating:
      return <DoorIcon color={color} />
    case IncidentType.LineCrossing:
      return <CameraIcon color={color} />
    case IncidentType.Alarm:
      // Door Open Alarms (matched by name when slug-based types are off) use
      // the door glyph, mirroring the legacy IncidentIcon behaviour.
      return isDoorOpenAlarmName(name) ? (
        <DoorIcon color={color} />
      ) : (
        <AlarmIcon color={color} />
      )
    default:
      return deviceType ? (
        getIconForDeviceType(deviceType, colorScheme)
      ) : (
        <ManualIcon colorScheme={colorScheme} />
      )
  }
}
